"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import {
  applyBitrixWidgetBranding,
  BITRIX_LIVECHAT_THEME_CSS,
  ensureBitrixEmojiSmilesSet,
  markLivechatSendStarted,
  resolveBitrixLivechatHeaderLogo,
  resolveBitrixLivechatPalette,
  syncBitrixCookieBannerLauncherOffset,
  syncBitrixLauncherIdleState,
  syncBitrixLikeButtonStyles,
  syncBitrixSendButtonStyle,
  syncLivechatStartedView,
} from "@/lib/brand/bitrix-livechat-theme";
import { COOKIE_BANNER_OFFSET_EVENT } from "@/lib/consent/cookie-banner-offset";
import {
  BITRIX_LAUNCHER_LEFT_MAX_WIDTH_PX,
  isBitrix24LauncherCornerRequested,
  isBitrix24SiteButtonPath,
  setBitrix24LauncherCornerSource,
  setBitrix24WidgetHidden,
  shouldBitrix24LauncherLeft,
} from "@/lib/bitrix24/site-button";
import { useBrandLogoVariant } from "@/components/brand/logo-context";
import { useResolvedDarkMode } from "@/lib/theme/use-resolved-dark";

/**
 * Брендинг Bitrix24 livechat: CSS + JS (перебивает inline !important Bitrix).
 */
export function Bitrix24LivechatTheme() {
  const pathname = usePathname();
  const isDark = useResolvedDarkMode();
  const logoVariant = useBrandLogoVariant();
  const enabled = isBitrix24SiteButtonPath(pathname);
  const launcherLeft = enabled && shouldBitrix24LauncherLeft(pathname);

  useEffect(() => {
    setBitrix24WidgetHidden(!enabled);
    return () => setBitrix24WidgetHidden(false);
  }, [enabled]);

  useEffect(() => {
    setBitrix24LauncherCornerSource("path", launcherLeft);
    if (launcherLeft) {
      syncBitrixCookieBannerLauncherOffset();
    }
    return () => setBitrix24LauncherCornerSource("path", false);
  }, [launcherLeft]);

  useEffect(() => {
    if (!enabled) return;

    const media = window.matchMedia(
      `(max-width: ${BITRIX_LAUNCHER_LEFT_MAX_WIDTH_PX}px)`,
    );
    const onViewportChange = () => {
      if (!isBitrix24LauncherCornerRequested()) return;
      syncBitrixCookieBannerLauncherOffset();
    };
    media.addEventListener("change", onViewportChange);
    return () => media.removeEventListener("change", onViewportChange);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;

    const palette = resolveBitrixLivechatPalette(isDark);
    const logo = resolveBitrixLivechatHeaderLogo(logoVariant, isDark);
    let frame = 0;

    const scheduleCookieOffset = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => syncBitrixCookieBannerLauncherOffset());
    };

    const scheduleLikeButtons = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => syncBitrixLikeButtonStyles(palette));
    };

    const run = () => {
      applyBitrixWidgetBranding(palette, logo);
      syncBitrixLauncherIdleState();
      ensureBitrixEmojiSmilesSet();
      syncLivechatStartedView();
      syncBitrixSendButtonStyle();
      syncBitrixCookieBannerLauncherOffset();
      syncBitrixLikeButtonStyles(palette);
    };

    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(run);
    };

    const scheduleChatView = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        syncLivechatStartedView();
        syncBitrixSendButtonStyle();
      });
    };

    const scheduleSendButton = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => syncBitrixSendButtonStyle());
    };

    const onSmilesToggle = (event: Event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (!target.closest(".bx-im-textarea-app-button")) return;
      window.setTimeout(() => ensureBitrixEmojiSmilesSet(), 0);
      window.setTimeout(() => ensureBitrixEmojiSmilesSet(), 120);
    };

    const onLikeClick = (event: Event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (
        !target.closest(
          ".bx-livechat-btn-like, .bx-livechat-btn-dislike, .bx-im-message-like, .bx-livechat-control-btn",
        )
      ) {
        return;
      }
      window.setTimeout(() => scheduleLikeButtons(), 0);
      window.setTimeout(() => scheduleLikeButtons(), 180);
    };

    run();

    document.addEventListener("click", onSmilesToggle, true);
    document.addEventListener("click", onLikeClick, true);

    const onChatSend = (event: Event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      if (event.type === "click") {
        if (!target.closest(".bx-im-textarea-send-button")) return;
      } else if (event.type === "keydown") {
        const keyEvent = event as KeyboardEvent;
        if (keyEvent.key !== "Enter" || keyEvent.shiftKey) return;
        if (!target.classList.contains("bx-im-textarea-input")) return;
      } else {
        return;
      }

      markLivechatSendStarted();
      window.setTimeout(() => syncLivechatStartedView(), 150);
      window.setTimeout(() => syncLivechatStartedView(), 600);
    };

    document.addEventListener("click", onChatSend, true);
    document.addEventListener("keydown", onChatSend, true);

    const observer = new MutationObserver((mutations) => {
      const relevant = mutations.some((m) => {
        if (m.type === "attributes") {
          const target = m.target;
          if (!(target instanceof HTMLElement)) return false;

          if (
            m.attributeName === "style" &&
            target.matches(
              ".bx-im-textarea-send-button, .b24-widget-button-popup, .b24-widget-button-popup-triangle",
            )
          ) {
            if (target.matches(".bx-im-textarea-send-button")) {
              scheduleSendButton();
            } else {
              schedule();
            }
            return false;
          }

          if (m.attributeName !== "class") return false;
          if (
            target.matches(
              ".bx-livechat-btn-like, .bx-livechat-btn-dislike, .bx-im-message-like, .bx-livechat-control-btn",
            )
          ) {
            scheduleLikeButtons();
            return false;
          }
          return (
            target.matches(
              ".bx-livechat-wrapper, .b24-widget-button-wrapper, .b24-widget-button-inner-container, .b24-widget-button-inner-block, .b24-widget-button-popup, .bx-ui-smiles-box, .bx-livechat-alert-box, .bx-livechat-alert-smiles-box, .bx-ui-smiles-set, .ix-bx-logo-lockup, .bx-livechat-dialog, .bx-livechat-body, .b24-form-wrapper, .b24-form-sign, .bx-imopenlines-form-wrapper",
            ) ||
              target.closest(
                ".bx-livechat-wrapper, .b24-widget-button-wrapper",
              )
          );
        }

        if (m.type !== "childList") return false;

        const target = m.target;
        if (!(target instanceof HTMLElement)) return false;

        const isMessageMutation =
          target.matches(
            ".bx-livechat-dialog, .bx-messenger-content, .bx-messenger-content-item, .bx-im-message, .bx-im-message-list, .bx-im-dialog, .bx-messenger-box",
          ) ||
          target.closest(
            ".bx-livechat-dialog, .bx-messenger-content, .bx-im-message-list, .bx-im-dialog, .bx-messenger-box",
          );

        if (isMessageMutation) {
          scheduleChatView();
          return false;
        }

        const nodes = [...m.addedNodes, ...m.removedNodes];
        return nodes.some((node) => {
          if (!(node instanceof HTMLElement)) return false;
          if (
            node.matches(
              ".bx-messenger-content-item, .bx-im-message, .bx-im-message-content",
            )
          ) {
            scheduleChatView();
            return false;
          }
          return Boolean(
            node.closest(".bx-livechat-wrapper, .b24-widget-button-wrapper") ||
              node.querySelector?.(
                ".bx-livechat-wrapper, .b24-widget-button-wrapper, .b24-widget-button-popup, .bx-ui-smiles-box, .bx-livechat-alert-box",
              ),
          );
        });
      });
      if (relevant) schedule();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style"],
    });

    const onBitrixLiveChat = (event: Event) => {
      const detail = (event as CustomEvent<{ widget?: { subscribe?: Function } }>)
        .detail;
      const widget = detail?.widget;
      if (!widget?.subscribe) return;

      widget.subscribe({
        type: "every",
        callback: () => syncBitrixLauncherIdleState(),
      });
      syncBitrixLauncherIdleState();
    };

    window.addEventListener("onBitrixLiveChat", onBitrixLiveChat);
    window.addEventListener(COOKIE_BANNER_OFFSET_EVENT, scheduleCookieOffset);

    return () => {
      observer.disconnect();
      document.removeEventListener("click", onSmilesToggle, true);
      document.removeEventListener("click", onLikeClick, true);
      document.removeEventListener("click", onChatSend, true);
      document.removeEventListener("keydown", onChatSend, true);
      cancelAnimationFrame(frame);
      window.removeEventListener("onBitrixLiveChat", onBitrixLiveChat);
      window.removeEventListener(COOKIE_BANNER_OFFSET_EVENT, scheduleCookieOffset);
      delete document.documentElement.dataset.ixBxChatOpen;
      document
        .querySelectorAll(".b24-widget-button-inner-container.ix-bx-launcher-idle")
        .forEach((node) => node.classList.remove("ix-bx-launcher-idle"));
    };
  }, [enabled, isDark, logoVariant]);

  if (!enabled) return null;

  return (
    <style
      id="idealitika-bitrix-livechat-theme"
      // eslint-disable-next-line react/no-danger -- third-party widget overrides
      dangerouslySetInnerHTML={{ __html: BITRIX_LIVECHAT_THEME_CSS }}
    />
  );
}
